import React, { useState, useEffect } from 'react';
import { apiRequest } from '../lib/api';
import { formatHhmm12h, formatAttendanceOrHhmm12h } from '../utils/dateUtils';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Compact "My Shifts" widget for staff dashboards
 * Shows today's shift + next few upcoming shifts
 */
export default function MyShiftsDashboard({ selectedClubId }) {
  const [todayShift, setTodayShift] = useState(null);
  const [upcomingShifts, setUpcomingShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedClubId) {
      loadMyShifts();
      // Refresh every 5 minutes
      const interval = setInterval(loadMyShifts, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [selectedClubId]);

  const calendarKeyKolkata = (dateInput) => {
    const d = new Date(dateInput);
    return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  };

  const loadMyShifts = async () => {
    try {
      const today = new Date();
      const endDate = new Date(today);
      endDate.setDate(endDate.getDate() + 7);

      const startDateStr = today.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      const response = await apiRequest(
        `/clubs/${selectedClubId}/shifts?startDate=${startDateStr}&endDate=${endDateStr}`
      );

      if (response.success) {
        const shifts = response.shifts || [];
        const todayKey = calendarKeyKolkata(today);

        const today_shift = shifts.find((s) => calendarKeyKolkata(s.shiftDate) === todayKey);
        setTodayShift(today_shift || null);

        // After today: one row per calendar day (Kolkata) — avoids duplicate "Tomorrow" if DB has two rows for same day
        const future = shifts
          .filter((s) => calendarKeyKolkata(s.shiftDate) > todayKey)
          .sort((a, b) => new Date(a.shiftDate) - new Date(b.shiftDate));

        const seenDays = new Set();
        const upcoming = [];
        for (const s of future) {
          const k = calendarKeyKolkata(s.shiftDate);
          if (seenDays.has(k)) continue;
          seenDays.add(k);
          upcoming.push(s);
          if (upcoming.length >= 3) break;
        }
        setUpcomingShifts(upcoming);
      }
    } catch (error) {
      console.error('Error loading shifts:', error);
    } finally {
      setLoading(false);
    }
  };

  const shiftTimeRangeLabel = (shift) => {
    if (shift.isOffDay) return null;
    if (shift.shiftStartDisplay && shift.shiftEndDisplay) {
      const start = formatHhmm12h(String(shift.shiftStartDisplay).slice(0, 5));
      const end = formatHhmm12h(String(shift.shiftEndDisplay).slice(0, 5));
      const nd = shift.shiftCrossesMidnight ? (
        <span className="ml-1 inline-flex items-center rounded-full border border-violet-400/35 bg-violet-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-violet-200">
          Next day
        </span>
      ) : null;
      return (
        <span className="inline-flex flex-wrap items-center gap-x-1 gap-y-1">
          <span>
            {start} – {end}
          </span>
          {nd}
        </span>
      );
    }
    return (
      <span>
        {formatAttendanceOrHhmm12h(shift.shiftStartTime)} –{' '}
        {formatAttendanceOrHhmm12h(shift.shiftEndTime)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const isToday = date.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) === today.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
      const isTomorrow = date.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) === tomorrow.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

      if (isToday) return 'Today';
      if (isTomorrow) return 'Tomorrow';

      return `${WEEKDAYS[date.getDay()]}, ${date.getDate()}`;
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg p-4">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          <div className="h-20 bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <span>⏰</span>
          My Shifts
        </h3>
        <button
          onClick={loadMyShifts}
          className="text-gray-400 hover:text-white transition-colors"
          title="Refresh"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Today's Shift - Highlighted */}
      {todayShift ? (
        <div className={`rounded-lg p-3 mb-3 ${
          todayShift.isOffDay
            ? 'bg-gray-700 border border-gray-600'
            : 'bg-gradient-to-r from-orange-600 to-orange-500'
        }`}>
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold text-white flex items-center gap-2">
              Today
              {!todayShift.isOffDay && (
                <span className="px-1.5 py-0.5 bg-white/20 text-white text-xs rounded">
                  ACTIVE
                </span>
              )}
            </span>
          </div>
          
          {todayShift.isOffDay ? (
            <div className="text-gray-300 text-sm">
              🌴 Off Day
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap text-white font-bold text-lg">
              {shiftTimeRangeLabel(todayShift)}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-gray-700/50 rounded-lg p-3 mb-3 text-center">
          <p className="text-gray-400 text-sm">No shift scheduled today</p>
        </div>
      )}

      {/* Upcoming Shifts */}
      {upcomingShifts.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-400 mb-2">Upcoming</h4>
          <div className="space-y-2">
            {upcomingShifts.map(shift => (
              <div
                key={shift.id}
                className="bg-gray-700/50 rounded p-2 text-sm"
              >
                <div className="flex justify-between items-center mb-1">
                  <span className="text-white font-medium">
                    {formatDate(shift.shiftDate)}
                  </span>
                  {shift.isOffDay && (
                    <span className="px-1.5 py-0.5 bg-gray-600 text-gray-300 text-xs rounded">
                      OFF
                    </span>
                  )}
                </div>
                {!shift.isOffDay && (
                  <div className="text-gray-300 text-xs">{shiftTimeRangeLabel(shift)}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!todayShift && upcomingShifts.length === 0 && (
        <div className="text-center py-4">
          <p className="text-gray-400 text-sm">No shifts scheduled</p>
          <p className="text-gray-500 text-xs mt-1">
            Contact your manager
          </p>
        </div>
      )}
    </div>
  );
}
